import React from "react";
import {
  Box,
  Container,
  Flex,
  Stack,
  SimpleGrid,
  Heading,
  Text,
  Image,
  Link,
  Divider,
  Badge,
  List,
  ListItem,
  AspectRatio,
  HStack,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";

import { newsItems } from "../DATABASE/newsList";

export default function HomePage() {
  const uoRed = "#8F001A";
  const uoRedHover = "#6E0014";

  const pageBg = useColorModeValue("white", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const subtleBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const text = useColorModeValue("gray.700", "gray.200");
  const muted = useColorModeValue("gray.600", "gray.400");

  const sectionCardProps = {
    bg: cardBg,
    border: `1px solid ${border}`,
    borderRadius: "20px",
    boxShadow: useColorModeValue("sm", "none"),
  };

  const linkStyle = {
    color: uoRed,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 700,
    _hover: { color: uoRedHover, textDecorationThickness: "2px" },
  };

  // ✅ Only for the "SCVIC & NEXTCON Lab" link
  const greenLinkStyle = {
    color: useColorModeValue("green.700", "green.300"),
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 800,
    _hover: {
      color: useColorModeValue("green.800", "green.200"),
      textDecorationThickness: "2px",
    },
  };

  const pill = {
    border: `1px solid ${border}`,
    bg: subtleBg,
    px: 3,
    py: 1,
    borderRadius: "999px",
    fontWeight: 800,
    fontSize: "sm",
  };

  return (
    <Box bg={pageBg}>
      {/* =========================
          HERO / HEADER
      ========================== */}
      <Container maxW="1200px" pt={{ base: 6, md: 10 }} pb={{ base: 6, md: 8 }}>
        <Stack spacing={4} textAlign="center">
          {/* subtle uOttawa accent */}
          <Box mx="auto" w={{ base: "70px", md: "90px" }} h="5px" bg={uoRed} borderRadius="999px" />

          <Heading as="h1" fontSize={{ base: "26px", md: "38px" }} fontWeight="900" letterSpacing="-0.3px">
            Burak Kantarci, PhD., P.Eng., SMIEEE, SM-ACM
          </Heading>

          <Text
            fontSize={{ base: "15px", md: "18px" }}
            color={muted}
            fontWeight="700"
            fontStyle="italic"
            lineHeight="1.6"
          >
            Professor &amp; University Research Chair in AI-Enabled Secure Networking for Smart Critical Infrastructures
            <br />
            IEEE Communications Society Distinguished Lecturer &nbsp;|&nbsp; IEEE Systems Council Distinguished Lecturer
          </Text>


          <Divider pt={2} />
        </Stack>
      </Container>

      {/* =========================
          DIRECTOR / CONTACT CARD
      ========================== */}
      <Container maxW="1200px" pb={{ base: 8, md: 10 }}>
        <Box {...sectionCardProps} overflow="hidden">
          <SimpleGrid columns={{ base: 1, md: 12 }} gap={0}>
            {/* Photo */}
            <Box
              gridColumn={{ base: "1 / -1", md: "1 / span 4" }}
              p={{ base: 5, md: 7 }}
              bg={useColorModeValue("gray.50", "whiteAlpha.50")}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Image
                src="BurakNewProfile.jpeg"
                alt="Burak Kantarci"
                borderRadius="16px"
                w={{ base: "260px", md: "100%" }}
                maxW={{ base: "260px", md: "320px" }}
                objectFit="cover"
                border={`1px solid ${border}`}
                boxShadow={useColorModeValue("md", "none")}
              />
            </Box>

            {/* Address */}
            <Box gridColumn={{ base: "1 / -1", md: "5 / span 4" }} p={{ base: 5, md: 7 }}>
              <VStack align={{ base: "center", md: "flex-start" }} spacing={2}>
                <Heading as="h2" fontSize={{ base: "18px", md: "20px" }} fontWeight="900" color={text}>
                  University of Ottawa
                </Heading>
                <Text color={muted} fontWeight="700" textAlign={{ base: "center", md: "left" }}>
                  School of Electrical Engineering and Computer Science
                </Text>
                <Text color={muted} fontWeight="600">
                  Ottawa, ON, K1N 6N5
                </Text>
                <Text color={muted} fontWeight="600">
                  Phone: (613) 562-5800 ext 6955
                </Text>
                <Text color={muted} fontWeight="700">
                  Email:{" "}
                  <Link href="mailto:burak.kantarci@uOttawa.ca" {...linkStyle}>
                    burak.kantarci@uOttawa.ca
                  </Link>
                </Text>
              </VStack>
            </Box>

            {/* Quick links */}
            <Box
              gridColumn={{ base: "1 / -1", md: "9 / span 4" }}
              p={{ base: 5, md: 7 }}
              bg={useColorModeValue("white", "gray.850")}
              borderLeft={{ base: "none", md: `1px solid ${border}` }}
            >
              <VStack align={{ base: "center", md: "flex-start" }} spacing={3}>
                <Heading as="h3" fontSize="16px" fontWeight="900" color={text}>
                  Quick Links
                </Heading>

                <HStack spacing={2} flexWrap="wrap" justify={{ base: "center", md: "flex-start" }}>
                  <Link href="/CVKantarciBurak 3.pdf" target="_blank" rel="noopener noreferrer" {...linkStyle}>
                    Curriculum Vitae (PDF)
                  </Link>
                  <Text color={muted} fontWeight="700">
                    •
                  </Text>
                  {/* ✅ green link requested */}
                  <Link href="https://nextconlab.ca/" target="_blank" rel="noopener noreferrer" {...greenLinkStyle}>
                    SCVIC &amp; NEXTCON Lab
                  </Link>
                </HStack>

                <Box w="100%" pt={2}>
                  <Text color={muted} fontWeight="900" mb={2}>
                    External
                  </Text>

                  <List spacing={2} pl={4} styleType="disc">
                    <ListItem>
                      <Link
                        href="https://scholar.google.com/citations?user=NBFqTFcAAAAJ&hl=en"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.800"
                        fontWeight="700"
                        textDecoration="underline"
                        _hover={{ color: "blue.900" }}
                      >
                        Google Scholar
                      </Link>
                    </ListItem>

                    <ListItem>
                      <Link
                        href="https://www.linkedin.com/in/bkantarci/"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.800"
                        fontWeight="700"
                        textDecoration="underline"
                        _hover={{ color: "blue.900" }}
                      >
                        LinkedIn
                      </Link>
                    </ListItem>

                    <ListItem>
                      <Link
                        href="https://www.researchgate.net/profile/Burak-Kantarci"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.800"
                        fontWeight="700"
                        textDecoration="underline"
                        _hover={{ color: "blue.900" }}
                      >
                        ResearchGate
                      </Link>
                    </ListItem>
                  </List>
                </Box>
              </VStack>
            </Box>
          </SimpleGrid>

          <Divider borderColor={border} />

          {/* Join us paragraph */}
          <Box px={{ base: 5, md: 7 }} py={{ base: 5, md: 6 }}>
            <Text color={text} fontSize={{ base: "15px", md: "16px" }} lineHeight="1.9" textAlign="justify">
              In <b>SCVIC and NEXTCON Lab</b>, my team and I are committed to creating an environment that is clearly
              supportive, diverse, gender-balanced, collegial and open to the researchers with strongest technical
              skills regardless of their background. I am looking for enthusiastic PhD students and Postdoctoral Fellows
              who will work on the topics listed below. Interested students or postdoc applicants may contact me via{" "}
              <Link href="mailto:scvic-civic@uottawa.ca" {...linkStyle}>
                scvic-civic@uottawa.ca
              </Link>{" "}
              to inquire about joining my team.
            </Text>
          </Box>
        </Box>
      </Container>

      {/* =========================
          MAIN BODY
      ========================== */}
      <Container maxW="1200px" pb={{ base: 10, md: 12 }}>
        <SimpleGrid columns={{ base: 1, lg: 12 }} gap={{ base: 6, md: 8 }}>
          {/* LEFT COLUMN */}
          <Box gridColumn={{ base: "1 / -1", lg: "1 / span 6" }}>
            <Stack spacing={6}>
              {/* Video */}
              <Box {...sectionCardProps} p={{ base: 4, md: 5 }}>
                <Heading as="h3" fontSize="18px" fontWeight="900" mb={3} color={text}>
                  Featured Video
                </Heading>
                <AspectRatio ratio={16 / 9} borderRadius="16px" overflow="hidden" border={`1px solid ${border}`}>
                  <iframe
                    src="https://www.youtube.com/embed/UZjw23oOAAk"
                    title="YouTube video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </AspectRatio>
              </Box>

              {/* Research interests */}
              <Box {...sectionCardProps} p={{ base: 4, md: 5 }}>
                <Flex align="center" justify="space-between" wrap="wrap" gap={2} mb={2}>
                  <Heading as="h3" fontSize="18px" fontWeight="900" color={text}>
                    Current Research Interests
                  </Heading>
                </Flex>

                <List spacing={3} mt={4}>
                  {[
                    "Semantic communications, Generative AI and Large Language Models for Next Generation Networks",
                    "AI-driven Security for Smart Critical Infrastructures",
                    "Secure, Resilient and Reliable Communications for 6G and Beyond",
                    "AI-backed event detection, actuation and planning for Connected Vehicles",
                    "Internet of Things / Internet of Vehicles",
                    "Crowdsensing and vehicular social networks",
                  ].map((x, i) => (
                    <ListItem key={i} color={text} lineHeight="1.7">
                      <Box
                        as="span"
                        display="inline-block"
                        w="10px"
                        h="10px"
                        bg={uoRed}
                        transform="rotate(45deg)"
                        mr={3}
                        borderRadius="2px"
                      />
                      <Text as="span" fontWeight="800" color={text}>
                        {x}
                      </Text>
                    </ListItem>
                  ))}
                </List>

                <Text mt={4} color={muted} fontWeight="700">
                  Looking for our surveys or tutorials in these areas?{" "}
                  <Link href="https://nextconlab.ca/surveys-tutorials" target="_blank" rel="noopener noreferrer" {...linkStyle}>
                    link
                  </Link>
                </Text>
              </Box>

              {/* News */}
              <Box {...sectionCardProps} p={{ base: 4, md: 5 }}>
                <Heading as="h3" fontSize="18px" fontWeight="900" mb={4} color={text}>
                  Top-15: Latest News
                </Heading>

                {/* ✅ architecture fix: constrain height + scroll inside */}
                <Box
                  maxH={{ base: "520px", md: "620px" }}
                  overflowY="auto"
                  pr={2}
                  sx={{
                    "&::-webkit-scrollbar": { width: "8px" },
                    "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.18)", borderRadius: "999px" },
                  }}
                >
                  <Stack spacing={3}>
                    {newsItems.slice(0, 15).map((item, idx) => (
                      <Box
                        key={idx}
                        position="relative"
                        bg={useColorModeValue("white", "gray.900")}
                        border={`1px solid ${border}`}
                        borderRadius="16px"
                        p={3}
                        pl={5}
                        _hover={{
                          borderColor: useColorModeValue("rgba(143,0,26,0.35)", "rgba(143,0,26,0.55)"),
                          boxShadow: useColorModeValue("0 10px 24px rgba(0,0,0,0.08)", "0 10px 24px rgba(0,0,0,0.35)"),
                        }}
                        transition="all 160ms ease"
                      >
                        {/* Accent strip */}
                        <Box
                          position="absolute"
                          left="0"
                          top="10px"
                          bottom="10px"
                          w="4px"
                          borderRadius="999px"
                          bg={uoRed}
                          opacity={0.95}
                        />

                        <Text fontWeight="900" color={text}>
                          {item.date}
                        </Text>

                        <Text mt={1} color={text} lineHeight="1.7">
                          {item.text}{" "}
                          {item.link ? (
                            <Link href={item.link} target="_blank" rel="noopener noreferrer" {...linkStyle}>
                              view.
                            </Link>
                          ) : null}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box pt={3}>
                  <Link href="https://www.tracemyip.org/" target="_blank" rel="noopener noreferrer">
                    <Image
                      src="tip.gif"
                      alt="TraceMyIP"
                      w="55px"
                      h="30px"
                      cursor="pointer"
                      borderRadius="6px"
                      border={`1px solid ${border}`}
                    />
                  </Link>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* RIGHT COLUMN (✅ sticky to avoid awkward whitespace feeling) */}
          <Box
            gridColumn={{ base: "1 / -1", lg: "7 / span 6" }}
            alignSelf="start"
            position={{ base: "static", lg: "sticky" }}
            top={{ base: "auto", lg: "110px" }} // adjust if your navbar height differs
          >
            <Box {...sectionCardProps} p={{ base: 4, md: 6 }}>
              <Heading as="h3" fontSize="18px" fontWeight="900" mb={4} color={text}>
                Short Bio
              </Heading>

              <Stack spacing={4} color={text} lineHeight="1.9" fontSize={{ base: "15px", md: "16px" }}>
                <Text textAlign="justify">
                  Dr. Burak Kantarci is a Full Professor and University Research Chair in AI-Enabled Secure Networking
                  for Smart Critical Infrastructures in the School of Electrical Engineering and Computer Science at the
                  University of Ottawa. Dr. Kantarci is the founding director of the Smart Connected Vehicles Innovation
                  Centre (<b>SCVIC</b>) and Next Generation Communications and Computing Networks Lab (<b>NEXTCON</b>).
                  Prior to joining uOttawa, he was an assistant professor at Clarkson University in Potsdam, New York
                  (2014–2016). In 2009–2014, he worked as a research fellow at the School of Electrical Engineering and
                  Computer Science at the University of Ottawa.
                </Text>

                <Text textAlign="justify">
                  Dr. Kantarci is a co-author of 300+ peer-reviewed publications. He has been listed among the{" "}
                  <b>top-2% cited scientists in telecommunications and networking</b> according to Stanford University
                  citation indicators since 2020, and has been listed among <b>Canada&apos;s top computer scientists</b>{" "}
                  by research.com since 2021. He is a recipient of multiple awards, including the <b>King Charles III
                  Coronation Medal</b> (2025), <b>Minister&apos;s Award of Excellence</b> (2021), and the{" "}
                  <b>IEEE Communications Software Technical Committee – 2023 Technical Achievement Award</b>, and a winner
                  of the <b>2024–25 George S. Glinski Award for Excellence in Research</b> in the Faculty of Engineering
                  at uOttawa.
                </Text>

                <Text textAlign="justify">
                  He has been the PI/co-PI of multiple research projects supported by NSERC, CFI, ORF, NSF, OCE, and
                  Mitacs. <b>[Total funding over $9.5M]</b>
                </Text>

                <Text textAlign="justify">
                  Dr. Kantarci has served in numerous editorial and leadership roles, including editorial positions at
                  IEEE Communications Surveys &amp; Tutorials, IEEE Networking Letters, IEEE Internet of Things Journal,
                  Vehicular Communications, IEEE Access, and IEEE Transactions on Green Communications and Networking.
                  In 2019–2020, he served as the Chair of the IEEE Communication Systems Integration and Modeling
                  Technical Committee of the IEEE Communications Society.
                </Text>

                <Text textAlign="justify">
                  Dr. Kantarci is a member of the{" "}
                  <Link href="https://ocics.ca/" target="_blank" rel="noopener noreferrer" {...linkStyle}>
                    Ottawa-Carleton Institute for Computer Science
                  </Link>{" "}
                  and the{" "}
                  <Link href="https://ociece.ca/bi_ling/" target="_blank" rel="noopener noreferrer" {...linkStyle}>
                    Ottawa-Carleton Institute for Electrical and Computer Engineering
                  </Link>
                  . He is a Professional Engineer (P.Eng) registered in the province of Ontario.
                </Text>
              </Stack>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* =========================
          FOOTER
      ========================== */}
      <Box borderTop={`1px solid ${border}`} bg={useColorModeValue("gray.50", "whiteAlpha.50")}>
        <Container maxW="1200px" py={6}>
          <Stack
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "center", md: "center" }}
            spacing={3}
          >
            <Text color={muted} fontWeight="700" textAlign={{ base: "center", md: "left" }}>
              © {new Date().getFullYear()} Burak Kantarci — University of Ottawa
            </Text>

            <HStack spacing={4} flexWrap="wrap" justify={{ base: "center", md: "flex-end" }}>
              <Link href="https://nextconlab.ca/" target="_blank" rel="noopener noreferrer" {...greenLinkStyle}>
                NEXTCON Lab
              </Link>
              <Link
                href="https://scholar.google.com/citations?user=NBFqTFcAAAAJ&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                {...linkStyle}
              >
                Google Scholar
              </Link>
              <Link href="/CVKantarciBurak 3.pdf" target="_blank" rel="noopener noreferrer" {...linkStyle}>
                CV (PDF)
              </Link>
            </HStack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}